import { createFileRoute } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Upload, Trash2, FileText, Film, Music, ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { useAuth } from '@/hooks/useAuth';
import {
  useMediaList,
  useUploadMedia,
  useDeleteMedia,
  mediaFileUrl,
  formatBytes,
  type MediaScope,
  type MediaFileType,
  type MediaRow,
} from '@/hooks/data/useMedia';

export const Route = createFileRoute('/_app/mediathek')({
  component: MediathekPage,
});

const TYPE_FILTERS: Array<{ key: MediaFileType | 'all'; label: string }> = [
  { key: 'all', label: 'Alle' },
  { key: 'image', label: 'Bilder' },
  { key: 'pdf', label: 'PDF' },
  { key: 'video', label: 'Video' },
  { key: 'audio', label: 'Audio' },
];

function MediathekPage() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const isAdmin = profile?.role === 'admin';
  const confirm = useConfirm();
  const fileRef = useRef<HTMLInputElement>(null);

  const [scope, setScope] = useState<MediaScope>('personal');
  const [type, setType] = useState<MediaFileType | 'all'>('all');

  const { data: items = [], isLoading } = useMediaList({
    scope,
    ...(type !== 'all' ? { type } : {}),
  });
  const upload = useUploadMedia();
  const del = useDeleteMedia();

  const onFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    let ok = 0;
    let fail = 0;
    for (const file of Array.from(files)) {
      try {
        await upload.mutateAsync({ file, scope });
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    if (ok) toast.success(t('mediathek.uploaded', '{{n}} Datei(en) hochgeladen.', { n: ok }));
    if (fail) toast.error(t('mediathek.upload_failed', '{{n}} fehlgeschlagen (zu groß oder nicht unterstützt).', { n: fail }));
    if (fileRef.current) fileRef.current.value = '';
  };

  const remove = async (m: MediaRow) => {
    const okConfirm = await confirm({
      title: t('mediathek.delete_title', 'Datei löschen?'),
      body: t('mediathek.delete_body', 'Die Datei wird entfernt.'),
      destructive: true,
    });
    if (!okConfirm) return;
    await del.mutateAsync(m.id);
  };

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl text-ink">{t('mediathek.title', 'Mediathek')}</h2>
          <p className="text-sm text-ink-3">
            {t('mediathek.subtitle', 'Zentrale Ablage für Bilder, PDFs, Videos und Audio.')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            multiple
            hidden
            accept="image/jpeg,image/png,image/webp,application/pdf,video/mp4,video/webm,audio/mpeg"
            onChange={(e) => onFiles(e.target.files)}
          />
          <Button onClick={() => fileRef.current?.click()} disabled={upload.isPending}>
            <Upload className="h-4 w-4" />
            {upload.isPending ? t('mediathek.uploading', 'Lädt hoch …') : t('mediathek.upload', 'Hochladen')}
          </Button>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-4">
        <div className="inline-flex rounded-lg border border-line p-0.5">
          {(['personal', 'shared'] as MediaScope[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setScope(s)}
              className={`rounded-md px-3 py-1 text-sm ${
                scope === s ? 'bg-ink text-paper' : 'text-ink-3 hover:bg-paper-2'
              }`}
            >
              {s === 'personal' ? t('mediathek.scope_personal', 'Meine') : t('mediathek.scope_shared', 'Geteilt')}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {TYPE_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setType(f.key)}
              className={`rounded-full border px-3 py-1 text-xs ${
                type === f.key ? 'border-transparent bg-ink text-paper' : 'border-line text-ink-3'
              }`}
            >
              {t(`mediathek.type_${f.key}`, f.label)}
            </button>
          ))}
        </div>
        {scope === 'shared' && !isAdmin && (
          <span className="text-xs text-ink-3">
            {t('mediathek.shared_readonly', 'Geteilte Dateien können nur Admins hochladen/löschen.')}
          </span>
        )}
      </div>

      {isLoading ? (
        <p className="text-sm text-ink-3">{t('common.loading', 'Lädt …')}</p>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-line p-10 text-center text-sm text-ink-3">
          {t('mediathek.empty', 'Keine Dateien. Laden Sie Ihre erste hoch.')}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {items.map((m) => (
            <MediaCard key={m.id} media={m} onDelete={() => remove(m)} canDelete={isAdmin || m.scope === 'personal'} />
          ))}
        </div>
      )}
    </section>
  );
}

function MediaCard({ media, onDelete, canDelete }: { media: MediaRow; onDelete: () => void; canDelete: boolean }) {
  const { t } = useTranslation();
  const url = mediaFileUrl(media.id);
  return (
    <div className="group relative overflow-hidden rounded-xl border border-line bg-card">
      <a href={url} target="_blank" rel="noopener noreferrer" className="block">
        <div className="flex aspect-square items-center justify-center bg-paper-2">
          {media.file_type === 'image' ? (
            <img src={url} alt={media.file_name} className="h-full w-full object-cover" loading="lazy" />
          ) : (
            <TypeIcon type={media.file_type} />
          )}
        </div>
      </a>
      {canDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="absolute end-1.5 top-1.5 rounded-md bg-paper/90 p-1 text-ink-3 opacity-0 transition group-hover:opacity-100 hover:text-[hsl(var(--danger))]"
          aria-label={t('common.delete', 'Löschen')}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
      <div className="p-2">
        <p className="truncate text-xs font-medium text-ink" title={media.file_name}>
          {media.file_name}
        </p>
        <p className="text-[10px] uppercase tracking-wide text-ink-3">
          {media.file_type} · {formatBytes(media.size_bytes)}
        </p>
      </div>
    </div>
  );
}

function TypeIcon({ type }: { type: MediaFileType }) {
  const cls = 'h-8 w-8 text-ink-3';
  if (type === 'pdf') return <FileText className={cls} />;
  if (type === 'video') return <Film className={cls} />;
  if (type === 'audio') return <Music className={cls} />;
  return <ImageIcon className={cls} />;
}
